import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const SITES = [
  { id: 'plant-1', name: 'Plant 1 — Manchester', coordinator: 'David Brown' },
  { id: 'plant-2', name: 'Plant 2 — Birmingham', coordinator: 'Maria Garcia' },
  { id: 'plant-3', name: 'Plant 3 — Leeds', coordinator: 'James Wilson' },
  { id: 'warehouse-1', name: 'Warehouse — Sheffield', coordinator: 'Emily Rodriguez' },
  { id: 'office-1', name: 'Head Office — London', coordinator: 'Robert Thompson' },
];

const sharingStatusConfig = {
  not_shared: { label: 'Not Shared', color: 'bg-slate-100 text-slate-600 dark:bg-slate-900/30 dark:text-slate-400' },
  shared: { label: 'Shared', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' },
};

const applicabilityConfig = {
  not_reviewed: { label: 'Not Reviewed', color: 'bg-slate-100 text-slate-600 dark:bg-slate-900/30 dark:text-slate-400' },
  applicable: { label: 'Applicable', color: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' },
  not_applicable: { label: 'Not Applicable', color: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' },
};

const implementationConfig = {
  not_started: { label: 'Not Started', color: 'bg-slate-100 text-slate-600 dark:bg-slate-900/30 dark:text-slate-400' },
  in_progress: { label: 'In Progress', color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' },
  implemented: { label: 'Implemented', color: 'bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-400' },
  verified: { label: 'Verified', color: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' },
};

const CrossSiteApplicabilityPanel = ({ recommendations, siteTracking, onUpdateTracking }) => {
  const [selectedRec, setSelectedRec] = useState(recommendations?.[0] || null);
  const [taggedSites, setTaggedSites] = useState([]);
  const [boardNotes, setBoardNotes] = useState('');
  const [decision, setDecision] = useState('');
  const [expandedSite, setExpandedSite] = useState(null);
  const [justifications, setJustifications] = useState({});

  const recTracking = siteTracking?.filter(t => t?.recommendationId === selectedRec?.id) || [];

  const handleTagSite = (siteId) => {
    setTaggedSites(prev =>
      prev?.includes(siteId) ? prev?.filter(s => s !== siteId) : [...prev, siteId]
    );
  };

  const handleShareToSites = () => {
    if (!taggedSites?.length || !decision) return;
    taggedSites?.forEach(siteId => {
      const site = SITES?.find(s => s?.id === siteId);
      onUpdateTracking?.({
        id: `${selectedRec?.id}-${siteId}`,
        recommendationId: selectedRec?.id,
        siteId,
        siteName: site?.name,
        coordinator: site?.coordinator,
        sharingStatus: 'shared',
        applicabilityStatus: 'not_reviewed',
        implementationStatus: 'not_started',
        decision,
        boardNotes,
        sharedAt: new Date()?.toISOString(),
      });
    });
    setTaggedSites([]);
    setBoardNotes('');
    setDecision('');
  };

  const handleApplicabilityUpdate = (trackingId, status, justification) => {
    onUpdateTracking?.({ id: trackingId, applicabilityStatus: status, justification });
  };

  const handleImplementationUpdate = (trackingId, status) => {
    onUpdateTracking?.({ id: trackingId, implementationStatus: status });
  };

  return (
    <div className="space-y-4">
      {/* Recommendation selector */}
      {recommendations?.length > 1 && (
        <div className="bg-card border border-border rounded-xl p-4">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">Select Recommendation</p>
          <div className="space-y-2">
            {recommendations?.map(rec => (
              <button
                key={rec?.id}
                onClick={() => setSelectedRec(rec)}
                className={`w-full text-left p-3 rounded-lg border text-sm transition-all ${
                  selectedRec?.id === rec?.id ? 'border-primary bg-primary/5' : 'border-border hover:bg-muted/30'
                }`}
              >
                <p className="font-medium text-foreground leading-snug">{rec?.description}</p>
                <p className="text-xs text-muted-foreground mt-0.5 capitalize">{rec?.type} · {rec?.priority} priority</p>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Global CAPA Board Decision */}
      <div className="bg-card border border-border rounded-xl p-5">
        <div className="flex items-center gap-2 mb-5">
          <div className="p-2 bg-indigo-100 dark:bg-indigo-900/30 rounded-lg">
            <Icon name="Globe" size={18} className="text-indigo-600 dark:text-indigo-400" />
          </div>
          <div>
            <h3 className="text-base font-semibold text-foreground">Global CAPA Board Review</h3>
            <p className="text-xs text-muted-foreground">Tag sites for cross-site applicability</p>
          </div>
        </div>

        {selectedRec && (
          <div className="mb-4 p-3 bg-muted/40 rounded-lg">
            <p className="text-xs text-muted-foreground mb-0.5">Reviewing recommendation:</p>
            <p className="text-sm font-medium text-foreground">{selectedRec?.description}</p>
          </div>
        )}

        <div className="space-y-4">
          <div>
            <label className="text-xs font-medium text-muted-foreground mb-2 block">Board Decision *</label>
            <div className="flex gap-2">
              {['site-specific', 'multi-site candidate']?.map(d => (
                <button
                  key={d}
                  onClick={() => setDecision(d)}
                  className={`flex-1 px-3 py-2 rounded-lg border text-xs font-medium transition-all capitalize ${
                    decision === d ? 'border-primary bg-primary/10 text-primary' : 'border-border text-muted-foreground hover:border-primary/40'
                  }`}
                >
                  {d === 'site-specific' ? '🏭 Site-Specific' : '🌐 Multi-Site Candidate'}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="text-xs font-medium text-muted-foreground mb-2 block">Tag Sites for Sharing</label>
            <div className="grid grid-cols-1 gap-2">
              {SITES?.map(site => {
                const alreadyShared = recTracking?.some(t => t?.siteId === site?.id && t?.sharingStatus === 'shared');
                return (
                  <label
                    key={site?.id}
                    className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                      alreadyShared ? 'border-emerald-200 bg-emerald-50 dark:bg-emerald-900/20 cursor-not-allowed' : taggedSites?.includes(site?.id) ?'border-primary bg-primary/5' : 'border-border hover:bg-muted/30'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={taggedSites?.includes(site?.id) || alreadyShared}
                      disabled={alreadyShared}
                      onChange={() => !alreadyShared && handleTagSite(site?.id)}
                      className="rounded"
                    />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-foreground">{site?.name}</p>
                      <p className="text-xs text-muted-foreground">Coordinator: {site?.coordinator}</p>
                    </div>
                    {alreadyShared && (
                      <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 text-xs font-medium rounded-full">Shared</span>
                    )}
                  </label>
                );
              })}
            </div>
          </div>

          <div>
            <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Board Notes</label>
            <textarea
              placeholder="Add context for site coordinators..."
              value={boardNotes}
              onChange={e => setBoardNotes(e?.target?.value)}
              rows={2}
              className="w-full text-sm px-3 py-2 bg-background border border-border rounded-lg outline-none focus:ring-2 focus:ring-primary/30 text-foreground placeholder:text-muted-foreground resize-none"
            />
          </div>

          <button
            onClick={handleShareToSites}
            disabled={!taggedSites?.length || !decision}
            className="w-full px-4 py-2.5 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-medium rounded-lg transition-colors flex items-center justify-center gap-2"
          >
            <Icon name="Share2" size={15} />
            Share to {taggedSites?.length} Site{taggedSites?.length !== 1 ? 's' : ''}
          </button>
        </div>
      </div>

      {/* Per-Site Tracking */}
      {recTracking?.length > 0 && (
        <div className="bg-card border border-border rounded-xl p-5">
          <div className="flex items-center gap-2 mb-4">
            <div className="p-2 bg-teal-100 dark:bg-teal-900/30 rounded-lg">
              <Icon name="LayoutGrid" size={18} className="text-teal-600 dark:text-teal-400" />
            </div>
            <h3 className="text-base font-semibold text-foreground">Per-Site Implementation Tracking</h3>
          </div>

          <div className="space-y-3">
            {recTracking?.map(tracking => {
              const sharing = sharingStatusConfig?.[tracking?.sharingStatus] || sharingStatusConfig?.not_shared;
              const applicability = applicabilityConfig?.[tracking?.applicabilityStatus] || applicabilityConfig?.not_reviewed;
              const implementation = implementationConfig?.[tracking?.implementationStatus] || implementationConfig?.not_started;
              const isExpanded = expandedSite === tracking?.id;
              const isOverdue = tracking?.dueDate && new Date(tracking.dueDate) < new Date() && tracking?.implementationStatus !== 'verified';

              return (
                <div key={tracking?.id} className={`border rounded-xl overflow-hidden ${isOverdue ? 'border-red-300 dark:border-red-800' : 'border-border'}`}>
                  <button
                    onClick={() => setExpandedSite(isExpanded ? null : tracking?.id)}
                    className="w-full flex items-center gap-3 p-4 text-left hover:bg-muted/30 transition-colors"
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <p className="text-sm font-semibold text-foreground">{tracking?.siteName}</p>
                        {isOverdue && <span className="px-2 py-0.5 bg-red-100 text-red-700 text-xs font-medium rounded-full flex items-center gap-1"><Icon name="AlertCircle" size={10} />Overdue</span>}
                      </div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${sharing?.color}`}>{sharing?.label}</span>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${applicability?.color}`}>{applicability?.label}</span>
                        {tracking?.applicabilityStatus === 'applicable' && (
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${implementation?.color}`}>{implementation?.label}</span>
                        )}
                      </div>
                    </div>
                    <Icon name={isExpanded ? 'ChevronUp' : 'ChevronDown'} size={16} className="text-muted-foreground flex-shrink-0" />
                  </button>

                  {isExpanded && (
                    <div className="px-4 pb-4 border-t border-border bg-muted/20 space-y-4 pt-3">
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <div>
                          <p className="text-xs text-muted-foreground mb-0.5">Coordinator</p>
                          <p className="font-medium text-foreground">{tracking?.coordinator}</p>
                        </div>
                        {tracking?.dueDate && (
                          <div>
                            <p className="text-xs text-muted-foreground mb-0.5">Due Date</p>
                            <p className={`font-medium ${isOverdue ? 'text-red-600' : 'text-foreground'}`}>
                              {new Date(tracking.dueDate)?.toLocaleDateString()}
                            </p>
                          </div>
                        )}
                      </div>

                      {/* Applicability decision */}
                      {tracking?.applicabilityStatus === 'not_reviewed' && (
                        <div>
                          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Applicability Decision</p>
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleApplicabilityUpdate(tracking?.id, 'applicable', '')}
                              className="flex-1 px-3 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-medium rounded-lg transition-colors"
                            >
                              ✓ Applicable
                            </button>
                            <button
                              onClick={() => setExpandedSite(tracking?.id + '-reject')}
                              className="flex-1 px-3 py-2 border border-red-300 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 text-xs font-medium rounded-lg transition-colors"
                            >
                              ✗ Not Applicable
                            </button>
                          </div>
                          {expandedSite === tracking?.id + '-reject' && (
                            <div className="mt-2 space-y-2">
                              <textarea
                                placeholder="Justification required (min. 20 characters)..."
                                value={justifications?.[tracking?.id] || ''}
                                onChange={e => setJustifications(p => ({ ...p, [tracking?.id]: e?.target?.value }))}
                                rows={2}
                                className="w-full text-sm px-3 py-2 bg-background border border-border rounded-lg outline-none focus:ring-2 focus:ring-primary/30 text-foreground placeholder:text-muted-foreground resize-none"
                              />
                              <button
                                onClick={() => {
                                  if ((justifications?.[tracking?.id] || '')?.length >= 20) {
                                    handleApplicabilityUpdate(tracking?.id, 'not_applicable', justifications?.[tracking?.id]);
                                    setExpandedSite(tracking?.id);
                                  }
                                }}
                                disabled={(justifications?.[tracking?.id] || '')?.length < 20}
                                className="px-3 py-1.5 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white text-xs font-medium rounded-lg transition-colors"
                              >
                                Confirm Not Applicable
                              </button>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Implementation tracking */}
                      {tracking?.applicabilityStatus === 'applicable' && (
                        <div>
                          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Implementation Status</p>
                          <div className="flex gap-2 flex-wrap">
                            {['in_progress', 'implemented', 'verified']?.map(s => (
                              <button
                                key={s}
                                onClick={() => handleImplementationUpdate(tracking?.id, s)}
                                className={`px-3 py-1.5 rounded-lg border text-xs font-medium transition-all ${
                                  tracking?.implementationStatus === s
                                    ? 'border-primary bg-primary/10 text-primary' :'border-border text-muted-foreground hover:border-primary/40'
                                }`}
                              >
                                {implementationConfig?.[s]?.label}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default CrossSiteApplicabilityPanel;
